import { type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PortalWordmark } from './route';

interface PortalLoginPanelProps {
  email: string;
  password: string;
  error?: string;
  /** Disable the inputs/button (used for the non-interactive preview). */
  disabled?: boolean;
  onEmailChange?: (value: string) => void;
  onPasswordChange?: (value: string) => void;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
}

/**
 * Presentational sign-in panel for the portal — the branded wordmark + a
 * sign-in card. Shared by the real `/portal/login` route and the "generated
 * portal" preview so both render the exact same screen. Theming/centering is
 * provided by the consumer (wrap in `PortalBrandTheme`).
 */
export function PortalLoginPanel({
  email,
  password,
  error,
  disabled,
  onEmailChange,
  onPasswordChange,
  onSubmit,
}: PortalLoginPanelProps) {
  return (
    <div className="w-full max-w-sm space-y-6">
      <PortalWordmark className="block text-center text-3xl font-semibold tracking-tight" />
      <Card>
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
          <CardDescription>Welcome to your customer portal.</CardDescription>
        </CardHeader>
        <CardContent>
          <form noValidate onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="portal-email">Email</Label>
              <Input
                id="portal-email"
                type="email"
                autoComplete="username"
                value={email}
                disabled={disabled}
                onChange={(e) => onEmailChange?.(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="portal-password">Password</Label>
              <Input
                id="portal-password"
                type="password"
                autoComplete="current-password"
                value={password}
                disabled={disabled}
                onChange={(e) => onPasswordChange?.(e.target.value)}
              />
            </div>
            {error && (
              <p
                className="text-sm"
                style={{ color: 'var(--destructive)' }}
                role="alert"
              >
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={disabled}>
              Sign in
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
